import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cookieUtils } from "@/lib/cookies";
import { useCart } from "@/contexts/CartContext";
import { ApiService } from "@/services/api";
import { 
  Loader2, 
  Code, 
  Palette, 
  Shield, 
  Database, 
  Cloud, 
  Briefcase, 
  FileText, 
  TrendingUp,
  Zap
} from "lucide-react";

interface JobPosition {
  id: string;
  name: string;
  description: string | null;
}

interface SearchAutocompleteProps {
  searchQuery: string;
  onSelect: () => void;
}

const getCategoryFromJobName = (jobName: string): string => {
  const name = jobName.toLowerCase();
  
  if (name.includes('frontend') || name.includes('react') || name.includes('vue') || name.includes('angular') || name.includes('ui')) {
    return 'Development';
  }
  if (name.includes('backend') || name.includes('api') || name.includes('python') || name.includes('java') || name.includes('node')) {
    return 'Development';
  }
  if (name.includes('full-stack') || name.includes('fullstack')) {
    return 'Development';
  }
  if (name.includes('mobile') || name.includes('ios') || name.includes('android') || name.includes('flutter')) {
    return 'Development';
  }
  if (name.includes('designer') || name.includes('ux') || name.includes('ui')) {
    return 'Design';
  }
  if (name.includes('qa') || name.includes('quality') || name.includes('test')) {
    return 'Quality Assurance';
  }
  if (name.includes('devops') || name.includes('cloud') || name.includes('architect') || name.includes('infrastructure')) {
    return 'Infrastructure';
  }
  if (name.includes('security') || name.includes('cyber')) {
    return 'Infrastructure';
  }
  if (name.includes('data') || name.includes('ai') || name.includes('ml') || name.includes('machine learning')) {
    return 'Data & Analytics';
  }
  if (name.includes('product') || name.includes('scrum') || name.includes('agile') || name.includes('business analyst')) {
    return 'Product & Management';
  }
  if (name.includes('writer') || name.includes('content') || name.includes('documentation')) {
    return 'Content & Documentation';
  }
  if (name.includes('seo') || name.includes('marketing') || name.includes('growth')) {
    return 'Marketing & Growth';
  }
  
  return 'Other';
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Development':
      return Code;
    case 'Design':
      return Palette;
    case 'Quality Assurance':
      return Shield;
    case 'Infrastructure':
      return Cloud;
    case 'Data & Analytics':
      return Database;
    case 'Product & Management':
      return Briefcase;
    case 'Content & Documentation':
      return FileText;
    case 'Marketing & Growth':
      return TrendingUp;
    default:
      return Zap;
  }
};

export const SearchAutocomplete = ({ searchQuery, onSelect }: SearchAutocompleteProps) => {
  const [results, setResults] = useState<JobPosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const cart = useCart();
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  const updatePosition = () => {
    // Use requestAnimationFrame to avoid forced reflow
    requestAnimationFrame(() => {
      const anchorEl = anchorRef.current?.parentElement;
      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    });
  };

  useEffect(() => {
    const searchPositions = async () => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setResults([]);
        setIsVisible(false);
        return;
      }

      setIsLoading(true);
      setIsVisible(true);

      try {
        const { data, error } = await supabase
          .from('job_positions')
          .select('id, name, description')
          .ilike('name', `%${searchQuery}%`)
          .limit(5);

        if (error) throw error;
        setResults(data || []);
      } catch (error) {
        console.error('Error searching job positions:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounce = setTimeout(() => {
      searchPositions();
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isVisible) {
      // Use requestAnimationFrame to ensure DOM is ready before reading layout
      requestAnimationFrame(() => {
        updatePosition();
      });
    }
  }, [isVisible, searchQuery]);

  useEffect(() => {
    let rafId: number | null = null;
    const onScrollOrResize = () => {
      // Throttle with requestAnimationFrame to avoid excessive reflows
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updatePosition();
      });
    };
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  const handleSelectPosition = async (position: JobPosition) => {
    try {
      // Check if user is authenticated
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to create an order",
          variant: "destructive",
        });
        navigate('/auth');
        return;
      }

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to create an order",
          variant: "destructive",
        });
        navigate('/auth');
        return;
      }

      // Create order using edge function
      const response = await fetch(
        'https://pbvjsixlqnuzcnqahbxu.supabase.co/functions/v1/create-order',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            company_name: 'Pending',
            contact_name: 'Pending',
            email: user.email || 'pending@example.com',
            company_type: 'startup',
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create order');
      }

      const { order } = await response.json();
      const orderId = order.id;
      cookieUtils.setOrderId(orderId);
      console.log('[SearchAutocomplete] Order ID saved:', orderId);

      // Add selected position to cart using CartContext
      await cart.addItem({
        job_position_id: position.id,
        quantity: 1,
        seniority_level: 2, // Senior
        english_level: 'fluent'
      });

      toast({
        title: "Success",
        description: "Position added to cart",
      });

      // Navigate to calculator (cart page)
      onSelect();
      navigate('/calculator');
    } catch (error) {
      console.error('Error creating order and cart:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create order",
        variant: "destructive",
      });
    }
  };

  if (!isVisible || !searchQuery.trim()) return null;

  return (
    <>
      <span ref={anchorRef} aria-hidden="true" />
      {isVisible && createPortal(
        <div 
          ref={containerRef}
          className="mt-2 bg-white border border-brand-light-gray rounded-lg shadow-card max-h-[300px] overflow-y-auto z-[9999] pointer-events-auto"
          style={{ position: 'absolute', top: dropdownPos.top + 8, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-5 w-5 animate-spin text-brand-lavender" />
            </div>
          ) : results.length > 0 ? (
            <ul className="py-2">
              {results.map((position) => {
                const category = getCategoryFromJobName(position.name);
                const CategoryIcon = getCategoryIcon(category);
                
                return (
                  <li key={position.id}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectPosition(position);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-brand-lavender/20 transition-all duration-200 cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-lavender/30 flex items-center justify-center group-hover:bg-brand-lavender/50 transition-colors">
                          <CategoryIcon className="h-4 w-4 text-brand-navy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-brand-navy group-hover:text-brand-navy/90">{position.name}</div>
                          {position.description && (
                            <div className="text-sm text-brand-charcoal/70 mt-1 line-clamp-1">
                              {position.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-brand-charcoal/70">
              No positions found for "{searchQuery}"
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};
